// GENERAL

import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// HOOKS
import { PageDataProvider } from "../../hooks/usePageDataContext";
import { DataTableContextProvider } from "../../components/ui/Tables/hooks/useDataTableContext";
import usePageListDetails from "../../hooks/usePageListDetails";
import { useHostName, usePrivilegeCheck } from "../../hooks";
import useExportableData from "../../components/ui/Tables/hooks/useExportableData";

// STORES
import { useTableStore } from "../../stores";

// COMPONENTS
import {
	BaseButton,
	BaseText,
	PrimaryTable,
	ResetFiltersButton,
	SecondaryButton,
	Section,
} from "../../components";

// UTILS
import { getFontAwesomeIcon } from "../../utils";
import { renderApprovalActionsModal } from "../../components/ui/Tables/PrimaryTable/helperFunctions/renderApprovalActionsModal";

// TYPES
import {
	TableDisplayData,
	AdditionalData,
} from "../../components/ui/Tables/types";

// VIEW MODEL
import { useOrdersPageViewModel } from "./viewModel/useOrdersPageViewModel";

const OrdersPage: React.FC = () => {
	const navigate = useNavigate();
	const { search, pathname } = useLocation();
	const { fields } = useOrdersPageViewModel();

	const hostName = useHostName();
	const { setSlug } = useTableStore();
	const { isAdmin } = usePrivilegeCheck();

	const [pageTitle, setPageTitle] = useState("Orders");
	const [_, setOrdersData, exportToCSV, isLoading] = useExportableData();
	const { pageMeta } = usePageListDetails("sales-orders-listing");
	const [hasModalTable, setHasModalTable] = useState<boolean>(true);
	const [resetKey, setResetKey] = useState(0);

	const [callFromFilter, setCallFromFilter] = useState<boolean>();
	const [additionalData, setAdditionalData] = useState<AdditionalData>(
		{} as AdditionalData,
	);

	const [tableDisplayData, setTableDisplayData] = useState<TableDisplayData>({
		hasInfiniteScroll: false,
		leftLockedColumns: -1,
		isNestedTable: false,
		hasNestedTable: false,
		suffixColumnIndexes: [-1],
		tableSlug: "sales-orders",
		tableType: "primary",
		leftOffset: 219,
		hasCellExpansion: [],
	} as TableDisplayData);
	const [isAdditionalDataLoaded, setIsAdditionalDataLoaded] =
		useState<boolean>(false);

	const slug = "sales-orders";
	const filterSlug = ["test-filter-slug"];
	const hasPermissions =
		pageMeta?.acl["sales-orders"]["navigation-edit-on-commerce"];

	const shouldRenderResetFilters = React.useMemo(() => {
		const params = new URLSearchParams(search);
		// look for any slug_XXX param other than _recordsPerPage or _page
		return Array.from(params.keys()).some((key) => {
			if (!key.startsWith(`${slug}_`)) return false;
			return !key.endsWith("_recordsPerPage") && !key.endsWith("_page");
		});
	}, [search, slug]);

	// Initialize the data for first load
	useEffect(() => {
		const params = new URLSearchParams(search);
		const isApprovalFilterActive = params.get("filters") === "approvals";

		// keep the button‐style in sync
		setCallFromFilter(isApprovalFilterActive);

		// rebuild additionalData whenever "?filters=approvals" toggles
		const base: AdditionalData = {
			slug: fields?.ordersListingAdditionalData?.slug || undefined,
			uuid: fields?.ordersListingAdditionalData?.uuid || undefined,
			isQuote: false,
			_status: isApprovalFilterActive
				? isAdmin
					? ["pendingInitialApproval", "pendingApproval"]
					: ["pendingApproval"]
				: undefined,
		};
		setAdditionalData(base);
		setIsAdditionalDataLoaded(true);

		// also ensure setSlug is called once (you can do this here or in its own effect)
		setSlug("sales-orders");
	}, [search, hostName.hostName, isAdmin, setSlug, fields]);

	const handleCallFromFilter = () => {
		const params = new URLSearchParams(search);
		const isActive = params.get("filters") === "approvals";

		if (isActive) {
			// remove `filters` and clear salesOrderStageId
			params.delete("filters");
			setAdditionalData((prev) => {
				const { _status, ...rest } = prev;
				return rest;
			});
		} else {
			// add `filters=approvals` and set salesOrderStageId
			params.set("filters", "approvals");
			setAdditionalData((prev) => ({
				...prev,
				_status: isAdmin
					? ["pendingInitialApproval", "pendingApproval"]
					: ["pendingApproval"],
			}));
		}

		navigate(`${pathname}?${params.toString()}`, { replace: true });
		setCallFromFilter(!isActive);
	};

	// SETTINGS FROM FIELDS
	const isApprovalsButtonEnabled = fields?.approvalsButton?.isEnabled;
	const isExportButtonEnabled = fields?.exportButton?.isEnabled;

	// TEXT FROM FIELDS
	const approvalsButtonLabel = fields?.approvalsButton?.label;

	// FIELDS TO VARS
	const approvalsActionModalFields = fields?.approvalActionModal;
	const quickApprovalActionModalFields = fields?.approvalActionOptions;

	// RENDER MODALS

	return (
		<div className=" px-[60px] h-full font-omnes-regular">
			<Section additionalClasses="flex justify-between py-6">
				<div className="flex items-center">
					{getFontAwesomeIcon("calculator", "solid")}
					<BaseText
						text={pageTitle}
						additionalClasses="pl-2"
						size="text-22px"
						color="text-navy-500"
						tag="h1"
						fontFamily="font-omnes-medium"
					/>
				</div>
				<div className="flex w-1/3 justify-end items-center">
					{isApprovalsButtonEnabled && hasPermissions && (
						<div className="flex">
							<BaseButton
								text={approvalsButtonLabel}
								icon={getFontAwesomeIcon("stamp", "regular")}
								onClick={handleCallFromFilter}
								iconClasses={`${
									callFromFilter
										? "text-white"
										: "text-primary"
								} pr-2`}
								bgColor={
									callFromFilter ? "bg-primary" : "bg-white"
								}
								fontColor={
									callFromFilter
										? "text-white"
										: "text-primary"
								}
								hoverBackground={`${
									callFromFilter
										? "hover:bg-primaryDefaultHover hover:text-primary "
										: "hover:bg-supply-blue-50"
								}  text-nowrap max-sm:size-8 max-sm:justify-center text-14px px-2 border-1 rounded-full`}
								textBreakpoint="max-sm:hidden"
							/>

							<div className="pl-4">
								{/* <div className="h-[26px] w-[1px] bg-gray-300 mx-2"></div> */}
								<ResetFiltersButton
									slug="sales-orders"
									allowedParams={[
										"Orders",
										"uuid",
										`${slug}:null`,
									]}
								/>
							</div>
						</div>
					)}

					{isExportButtonEnabled && (
						<>
							{isLoading ? (
								<div>
									<span className="fa-spin fa-beat-fade">
										{getFontAwesomeIcon("spinner", "solid")}
									</span>

									<span className="pl-2">Downloading</span>
								</div>
							) : (
								<BaseButton
									text="Export"
									bgColor="bg-primary"
									fontColor="text-white"
									hoverBackground="hover:bg-primaryHover"
									buttonSize="w-85px h-26px"
									borderColor="none"
									additionalClasses={`text-nowrap mx-5px max-sm:size-8 max-sm:justify-center text-14px`}
									shape="rounded-full"
									icon={getFontAwesomeIcon(
										"upFromBracket",
										"regular",
									)}
									iconBreakpoint="max-sm:pr-0"
									as="button"
									hasTextBreakpoint={true}
									textBreakpoint="max-sm:hidden"
									// @ts-ignore
									onClick={exportToCSV}
									fontFamily="font-omnes-medium"
									iconClasses="pr-2 text-white"
								/>
							)}
						</>
					)}
				</div>
			</Section>

			{isAdditionalDataLoaded && (
				<PageDataProvider pageMeta={pageMeta} pageTitle={pageTitle}>
					<DataTableContextProvider
						showStickyColumn
						hasInfiniteScroll={tableDisplayData?.hasInfiniteScroll}
						hasModalTable={false}
						rowsHaveModalOnClick={true}
						data={[]}
						key={resetKey}
					>
						<PrimaryTable
							filterSlug={filterSlug}
							callFromFilter={callFromFilter}
							pageTitle={""}
							hasModalTable={hasModalTable}
							setPageTitle={setPageTitle}
							setHasModalTable={setHasModalTable}
							setData={setOrdersData}
							additionalData={additionalData}
							slug="sales-orders"
							tableDisplayData={tableDisplayData}
							quickApprovalDecisionListFields={
								fields?.approvalActionOptions
							}
						/>
					</DataTableContextProvider>
				</PageDataProvider>
			)}
		</div>
	);
};

export default OrdersPage;
